import { useCallback, useEffect, useRef, useState } from "react";
import { FiArrowLeft, FiCheckCircle, FiChevronLeft, FiChevronRight, FiRefreshCw, FiRotateCcw, FiSearch } from "react-icons/fi";
import AdminUserDetail from "../components/AdminUserDetail";
import SupportThread from "../components/SupportThread";
import {
  getAdminSupportConversation,
  getAdminSupportConversations,
  markAdminSupportRead,
  sendAdminSupportReply,
  sendAdminSupportAttachment,
  adminSupportFileUrl,
  setAdminSupportStatus,
} from "../api/admin";
import { useTranslation } from "../context/TranslationContext";
import { adminTheme } from "../styles/adminTheme";

/**
 * The support inbox: everyone's conversations with Mintly support, shared by every admin.
 * Unread ones come first. Replies go out as "Mintly Support"; the other admins see who
 * wrote them. New messages arrive live on `socket`: the panel's own, signed in with the
 * admin's token and joined to the room every admin shares.
 *
 * @see mintly-back/src/controllers/adminSupportController
 */

const STATUSES = ["open", "closed", "all"];

function Avatar({ user, isDark }) {
  if (user?.avatar) {
    return <img src={user.avatar} alt="" className="h-9 w-9 shrink-0 rounded-full object-cover" />;
  }
  return (
    <span
      className={`grid h-9 w-9 shrink-0 place-items-center rounded-full text-sm font-bold ${
        "bg-fill-tertiary text-label-secondary"
      }`}
    >
      {String(user?.username || user?.email || "?").charAt(0).toUpperCase()}
    </span>
  );
}

const AdminSupportTab = ({ isDark, setToast, baseText, mutedText, cardBase, socket, onUnreadChange }) => {
  const { t, language } = useTranslation();
  const theme = adminTheme(isDark);
  const [status, setStatus] = useState("open");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState(null);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [thread, setThread] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [loadingEarlier, setLoadingEarlier] = useState(false);
  const [sending, setSending] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [live, setLive] = useState(false);
  const [openUser, setOpenUser] = useState(null);
  const selectedRef = useRef(null);
  selectedRef.current = selectedId;

  const locale = language === "ka" ? "ka-GE" : "en-GB";
  const when = (iso) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return new Date().toDateString() === d.toDateString()
      ? d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit", hour12: false })
      : d.toLocaleDateString(locale, { day: "numeric", month: "short" });
  };
  const nameOf = (user) => user?.username || user?.email || t("admin.supportDeletedUser");

  // Typing in the search box searches once it settles.
  useEffect(() => {
    const id = setTimeout(() => {
      setPage(1);
      setQuery(search.trim());
    }, 300);
    return () => clearTimeout(id);
  }, [search]);

  const loadList = useCallback(
    async ({ quiet = false } = {}) => {
      if (!quiet) setLoading(true);
      try {
        const res = await getAdminSupportConversations({ status, q: query || undefined, page, limit: 30 });
        setRows(res.data || []);
        setCounts(res.counts || null);
        setPages(res.pages || 1);
        onUnreadChange?.(res.counts?.unread || 0);
      } catch (err) {
        if (!quiet) setToast?.(err?.response?.data?.message || t("admin.supportLoadError"));
      } finally {
        if (!quiet) setLoading(false);
      }
    },
    [status, query, page, onUnreadChange, setToast, t]
  );

  useEffect(() => {
    loadList();
  }, [loadList]);

  // Many live events can land together; the list reloads once after them.
  const loadListRef = useRef(loadList);
  loadListRef.current = loadList;
  const reloadTimer = useRef(null);
  const reloadSoon = useCallback(() => {
    clearTimeout(reloadTimer.current);
    reloadTimer.current = setTimeout(() => loadListRef.current({ quiet: true }), 300);
  }, []);
  useEffect(() => () => clearTimeout(reloadTimer.current), []);

  const open = async (id) => {
    setSelectedId(id);
    setThread(null);
    setThreadLoading(true);
    try {
      const res = await getAdminSupportConversation(id);
      if (selectedRef.current !== id) return;
      setThread(res.data);
      reloadSoon();
    } catch (err) {
      setToast?.(err?.response?.data?.message || t("admin.supportLoadError"));
    } finally {
      setThreadLoading(false);
    }
  };

  useEffect(() => {
    if (!socket) return undefined;
    setLive(socket.connected);
    const onConnect = () => setLive(true);
    const onDisconnect = () => setLive(false);
    const onMessage = ({ conversation, message }) => {
      if (conversation && message && selectedRef.current === conversation.id) {
        setThread((prev) =>
          prev
            ? {
                ...prev,
                conversation,
                messages: prev.messages.some((m) => m.id === message.id) ? prev.messages : [...prev.messages, message],
              }
            : prev
        );
        // Open in front of us: read as it arrives.
        if (!message.fromSupport) markAdminSupportRead(conversation.id).catch(() => {});
      }
      reloadSoon();
    };
    const onRead = ({ conversationId, by, at }) => {
      if (by === "user" && selectedRef.current === conversationId) {
        setThread((prev) => (prev ? { ...prev, conversation: { ...prev.conversation, seenAt: at } } : prev));
      }
      reloadSoon();
    };
    const onStatus = ({ conversation }) => {
      if (conversation && selectedRef.current === conversation.id) {
        setThread((prev) => (prev ? { ...prev, conversation } : prev));
      }
      reloadSoon();
    };
    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("support_message", onMessage);
    socket.on("support_read", onRead);
    socket.on("support_status", onStatus);
    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("support_message", onMessage);
      socket.off("support_read", onRead);
      socket.off("support_status", onStatus);
    };
  }, [socket, reloadSoon]);

  /** Reply with a photo, video or file, and its caption; resolves true once sent. */
  const replyWithFile = async (file, caption, onProgress) => {
    if (!selectedId) return false;
    try {
      const res = await sendAdminSupportAttachment(selectedId, file, caption, onProgress);
      const { conversation, message } = res.data || {};
      setThread((prev) =>
        prev && message
          ? {
              ...prev,
              conversation: conversation || prev.conversation,
              messages: prev.messages.some((m) => m.id === message.id) ? prev.messages : [...prev.messages, message],
            }
          : prev
      );
      reloadSoon();
      return true;
    } catch (err) {
      setToast?.(err?.response?.data?.message || t("chatFiles.sendError", { name: file.name }));
      return false;
    }
  };

  const reply = async (content) => {
    if (!selectedId) return false;
    setSending(true);
    try {
      const res = await sendAdminSupportReply(selectedId, content);
      const { conversation, message } = res.data || {};
      setThread((prev) =>
        prev && message
          ? {
              ...prev,
              conversation: conversation || prev.conversation,
              messages: prev.messages.some((m) => m.id === message.id) ? prev.messages : [...prev.messages, message],
            }
          : prev
      );
      reloadSoon();
      return true;
    } catch (err) {
      setToast?.(err?.response?.data?.message || t("admin.supportSendError"));
      return false;
    } finally {
      setSending(false);
    }
  };

  const toggleStatus = async () => {
    const conv = thread?.conversation;
    if (!conv) return;
    const next = conv.status === "closed" ? "open" : "closed";
    setChangingStatus(true);
    try {
      const res = await setAdminSupportStatus(conv.id, next);
      if (res.data?.conversation) setThread((prev) => (prev ? { ...prev, conversation: res.data.conversation } : prev));
      setToast?.(next === "closed" ? t("admin.supportClosedToast") : t("admin.supportReopenedToast"));
      reloadSoon();
    } catch (err) {
      setToast?.(err?.response?.data?.message || t("admin.supportStatusError"));
    } finally {
      setChangingStatus(false);
    }
  };

  const loadEarlier = async () => {
    if (!thread?.messages?.length) return;
    setLoadingEarlier(true);
    try {
      const res = await getAdminSupportConversation(thread.conversation.id, thread.messages[0].createdAt);
      const earlier = res.data?.messages || [];
      setThread((prev) =>
        prev
          ? {
              ...prev,
              hasMore: !!res.data?.hasMore,
              messages: [...earlier.filter((m) => !prev.messages.some((p) => p.id === m.id)), ...prev.messages],
            }
          : prev
      );
    } catch (err) {
      setToast?.(err?.response?.data?.message || t("admin.supportLoadError"));
    } finally {
      setLoadingEarlier(false);
    }
  };

  const conv = thread?.conversation;
  const closed = conv?.status === "closed";
  const filterLabel = {
    open: t("admin.supportFilterOpen"),
    closed: t("admin.supportFilterClosed"),
    all: t("admin.supportFilterAll"),
  };
  const filterCount = { open: counts?.open, closed: counts?.closed, all: counts?.total };

  return (
    <div className="space-y-4">
      <p className={`max-w-3xl text-sm ${mutedText}`}>{t("admin.supportIntro")}</p>

      <div className="grid gap-4 lg:grid-cols-[minmax(280px,380px)_1fr]">
        {/* The inbox */}
        <section
          className={`${cardBase} flex h-[70vh] min-h-[420px] flex-col overflow-hidden rounded-2xl lg:h-[min(760px,calc(100vh-220px))] ${
            selectedId ? "hidden lg:flex" : "flex"
          }`}
        >
          <div className={`space-y-3 border-b p-4 ${theme.rowBorder}`}>
            <div className="relative">
              <FiSearch className={`pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 ${mutedText}`} />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("admin.supportSearch")}
                className={`w-full rounded-xl border py-2 pl-9 pr-3 text-sm outline-none ${theme.inputBase}`}
              />
            </div>
            <div className="flex items-center gap-1.5">
              {STATUSES.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => {
                    setPage(1);
                    setStatus(s);
                  }}
                  className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                    status === s
                      ? "bg-mint text-ink"
                      : isDark
                      ? "bg-white/[0.05] text-slate-300 hover:bg-white/[0.08]"
                      : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {filterLabel[s]}
                  {typeof filterCount[s] === "number" ? ` ${filterCount[s]}` : ""}
                </button>
              ))}
              <button
                type="button"
                onClick={() => loadList()}
                className={`ml-auto ${theme.iconBtn}`}
                aria-label={t("admin.supportRefresh")}
                title={live ? t("admin.supportLive") : t("admin.supportOffline")}
              >
                <span className="relative block">
                  <FiRefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                  <span
                    className={`absolute -right-1.5 -top-1.5 h-2 w-2 rounded-full ${live ? "bg-emerald-400" : "bg-slate-400"}`}
                    aria-hidden
                  />
                </span>
              </button>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto p-2">
            {!loading && rows.length === 0 && <p className={`p-6 text-center text-sm ${mutedText}`}>{t("admin.supportNone")}</p>}
            {rows.map((row) => {
              const active = row.id === selectedId;
              const unread = row.unread > 0;
              return (
                <button
                  key={row.id}
                  type="button"
                  onClick={() => open(row.id)}
                  className={`flex w-full items-start gap-3 rounded-xl px-3 py-2.5 text-left transition ${
                    active
                      ? isDark
                        ? "bg-mint/10"
                        : "bg-mint/15"
                      : "hover:bg-fill-quaternary"
                  }`}
                >
                  <Avatar user={row.user} isDark={isDark} />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-baseline gap-2">
                      <span className={`truncate text-sm ${unread ? "font-bold" : "font-medium"} ${baseText}`}>{nameOf(row.user)}</span>
                      <span className={`ml-auto shrink-0 text-[11px] ${unread ? "font-semibold text-mint" : mutedText}`}>
                        {when(row.lastMessageAt)}
                      </span>
                    </span>
                    <span className="mt-0.5 flex items-center gap-2">
                      <span className={`truncate text-xs ${unread ? baseText : mutedText}`}>
                        {row.lastMessage.fromSupport ? `${t("admin.supportPreviewSupport")} ` : ""}
                        {row.lastMessage.content}
                      </span>
                      {status === "all" && row.status === "closed" && (
                        <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold bg-fill-tertiary text-label-secondary`}>
                          {t("admin.supportClosedChip")}
                        </span>
                      )}
                      {unread && (
                        <span className="ml-auto grid h-5 min-w-5 shrink-0 place-items-center rounded-full bg-mint px-1.5 text-[11px] font-bold text-ink">
                          {row.unread}
                        </span>
                      )}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          {pages > 1 && (
            <div className={`flex items-center justify-between border-t px-4 py-2.5 ${theme.rowBorder}`}>
              <span className={`text-xs ${mutedText}`}>{t("admin.supportPage", { page, pages })}</span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className={`${theme.iconBtn} disabled:opacity-40`}
                  aria-label={t("common.previous")}
                >
                  <FiChevronLeft className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(pages, p + 1))}
                  disabled={page >= pages}
                  className={`${theme.iconBtn} disabled:opacity-40`}
                  aria-label={t("common.next")}
                >
                  <FiChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </section>

        {/* The conversation */}
        <section
          className={`${cardBase} h-[70vh] min-h-[420px] flex-col overflow-hidden rounded-2xl lg:flex lg:h-[min(760px,calc(100vh-220px))] ${
            selectedId ? "flex" : "hidden"
          }`}
        >
          {!selectedId ? (
            <div className={`flex flex-1 items-center justify-center p-6 text-center text-sm ${mutedText}`}>{t("admin.supportPickOne")}</div>
          ) : threadLoading || !conv ? (
            <div className={`flex flex-1 items-center justify-center text-sm ${mutedText}`}>{t("common.loading")}</div>
          ) : (
            <>
              <div className={`flex items-center gap-3 border-b px-4 py-3 ${theme.rowBorder}`}>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedId(null);
                    setThread(null);
                  }}
                  className={`${theme.iconBtn} lg:hidden`}
                  aria-label={t("admin.supportBack")}
                >
                  <FiArrowLeft className="h-4 w-4" />
                </button>
                <Avatar user={conv.user} isDark={isDark} />
                <div className="min-w-0 flex-1">
                  {conv.user ? (
                    <button
                      type="button"
                      onClick={() => setOpenUser(conv.user.id)}
                      className={`block max-w-full truncate text-left text-sm font-semibold ${baseText} hover:text-mint`}
                    >
                      {nameOf(conv.user)}
                    </button>
                  ) : (
                    <span className={`text-sm font-semibold ${mutedText}`}>{nameOf(null)}</span>
                  )}
                  <div className={`truncate text-[11px] ${mutedText}`}>{conv.user?.email}</div>
                </div>
                <span
                  className={`hidden shrink-0 rounded-lg px-2 py-0.5 text-xs font-semibold sm:inline ${
                    closed
                      ? "bg-fill-tertiary text-label-secondary"
                      : "bg-emerald-500/15 text-emerald-500"
                  }`}
                >
                  {closed ? t("admin.supportClosedChip") : t("admin.supportOpenChip")}
                </span>
                <button type="button" onClick={toggleStatus} disabled={changingStatus} className={theme.btnSecondary}>
                  {closed ? <FiRotateCcw className="h-4 w-4" /> : <FiCheckCircle className="h-4 w-4" />}
                  {closed ? t("admin.supportReopen") : t("admin.supportClose")}
                </button>
              </div>
              <SupportThread
                side="support"
                messages={thread.messages}
                isDark={isDark}
                seenAt={conv.seenAt}
                hasMore={thread.hasMore}
                loadingEarlier={loadingEarlier}
                onLoadEarlier={loadEarlier}
                onSend={reply}
                onSendFile={replyWithFile}
                fileUrl={(m, options) => adminSupportFileUrl(conv.id, m.id, options)}
                onFileError={setToast}
                sending={sending}
                placeholder={t("admin.supportReplyPlaceholder")}
                emptyText={t("admin.supportNone")}
                notice={closed ? t("admin.supportClosedNotice") : null}
                senderLabel={(m) =>
                  m.fromSupport
                    ? m.sender?.username
                      ? t("admin.supportFromAdmin", { name: m.sender.username })
                      : t("support.team")
                    : nameOf(conv.user)
                }
              />
            </>
          )}
        </section>
      </div>

      {openUser && <AdminUserDetail userId={openUser} isDark={isDark} setToast={setToast} onClose={() => setOpenUser(null)} />}
    </div>
  );
};

export default AdminSupportTab;
